<template>
  <div class="video-box">
    <video
        ref="videoElement"
        :src="hasSrcset ? null : resolvedContentUrl"
        preload="auto"
        playsinline
    >
      <template v-if="hasSrcset">
        <source
            v-for="source in orderedSources"
            :key="source.type"
            :src="source.url"
            :type="source.type"
        />
      </template>
    </video>
    <div class="countdown">{{ secondsLeft }}</div>
    <qr-container
        v-if="qrPosition"
        :link="qrLink"
        :size="150"
        class="video-qr"
        :class="qrPosition"
    />
  </div>
</template>

<script>
import QrContainer from '../components/QrContainer'
import { reportAdEvent } from '../services/adReport'
import { reportAdFailure } from '../services/adFailure'
import mediaFallback from '../mixins/mediaFallback'

// If the video hasn't started playing within this window, force-finish.
// Prevents the ad from hanging forever on a broken CDN response.
const PLAYBACK_WATCHDOG_MS = 10000

export default {
  name: 'VideoBox',
  mixins: [mediaFallback],
  components: {
    QrContainer
  },
  data () {
    return {
      secondsLeft: null,
      lastCountdownSecond: null
    }
  },
  props: {
    contentUrl: {
      type: String,
      required: true
    },
    endUrl: {
      type: String,
      required: true
    },
    startUrl: {
      type: String,
      default: ''
    },
    contentSrcset: {
      type: Object,
      default: null
    },
    qrPosition: {
      type: String,
      default: null
    },
    qrLink: {
      type: String,
      default: null
    },
    // Only for the failure report: which campaign was on screen when nothing was.
    taskId: {
      type: Number,
      default: null
    },
    // Signed route the ad-did-not-play report is posted to. Absent on a backend
    // older than it, and then only Sentry hears about the failure.
    failUrl: {
      type: String,
      default: ''
    }
  },
  computed: {
    hasSrcset () {
      return this.contentSrcset && Object.keys(this.contentSrcset).length > 0
    },
    // Force webm first regardless of object key order from the backend.
    // This preserves transparency (webm+VP9 supports alpha channel; mp4 does not).
    // Chromium will try sources in this order and stop at the first that loads.
    //
    // Reads resolvedContentSrcset rather than contentSrcset so the zone
    // fallback reaches the <source> list too - otherwise a retry would move the
    // bare :src and leave every source pointing at the zone that just failed.
    orderedSources () {
      if (!this.hasSrcset) return []
      const srcset = this.resolvedContentSrcset
      const preferredOrder = ['video/webm', 'video/ogg', 'video/mp4']
      const out = []
      for (const type of preferredOrder) {
        if (srcset[type]) {
          out.push({ type, url: srcset[type] })
        }
      }
      // Include any other types the backend might send that weren't in our list
      for (const [type, url] of Object.entries(srcset)) {
        if (!preferredOrder.includes(type)) {
          out.push({ type, url })
        }
      }
      return out
    }
  },
  mounted () {
    try {
      const el = this.$refs.videoElement
      this._finished = false
      this._mountedAt = Date.now()
      this.reportStart()

      this._onEnded = () => {
        this.reportEnd()
        this.emitFinished()
      }

      this._onTimeUpdate = () => {
        const video = this.$refs.videoElement
        if (!video || video.duration == null || !isFinite(video.duration)) return
        const sec = Math.round(video.duration - video.currentTime)
        if (sec !== this.lastCountdownSecond) {
          this.lastCountdownSecond = sec
          this.secondsLeft = sec
        }
      }

      this._onError = (e) => {
        console.error('video element error', e, el.error)

        // One retry on the other zone before giving up. A creative that 404s
        // will not appear on a second attempt, but a zone briefly unreachable
        // across the RU border is exactly what this recovers - and the finish
        // below is what turned that into an ad nobody saw.
        if (this.tryMediaFallback()) return

        this.reportFailure('video_error')
        this.emitFinished()
      }

      this._onStalled = () => {
        console.warn('video stalled')
        // Don't finish here — Chromium often recovers from stalls.
        // The watchdog will catch cases that don't.
      }

      this._onPlaying = () => {
        // Got first frame, we're safe. Clear the watchdog.
        if (this._watchdog) {
          clearTimeout(this._watchdog)
          this._watchdog = null
        }
      }

      el.addEventListener('ended', this._onEnded)
      el.addEventListener('timeupdate', this._onTimeUpdate)
      el.addEventListener('error', this._onError)
      el.addEventListener('stalled', this._onStalled)
      el.addEventListener('playing', this._onPlaying)

      // Watchdog: if nothing is playing after N seconds, give up and finish.
      // This prevents ads from silently hanging when the video source
      // is slow/broken/returns bad data.
      this._watchdog = setTimeout(() => {
        console.warn('video watchdog fired - no playback within', PLAYBACK_WATCHDOG_MS, 'ms')
        this._watchdog = null
        this.reportFailure('watchdog')
        this.emitFinished()
      }, PLAYBACK_WATCHDOG_MS)

      this.attemptPlay(el)
    } catch (e) {
      this.processError(e)
    }
  },
  beforeDestroy () {
    const el = this.$refs.videoElement
    if (el) {
      if (this._onEnded) el.removeEventListener('ended', this._onEnded)
      if (this._onTimeUpdate) el.removeEventListener('timeupdate', this._onTimeUpdate)
      if (this._onError) el.removeEventListener('error', this._onError)
      if (this._onStalled) el.removeEventListener('stalled', this._onStalled)
      if (this._onPlaying) el.removeEventListener('playing', this._onPlaying)
    }
    if (this._watchdog) {
      clearTimeout(this._watchdog)
      this._watchdog = null
    }
  },
  methods: {
    async attemptPlay (el) {
      try {
        await el.play()
      } catch (e) {
        console.warn('video play rejected, retrying muted:', e)
        // Fallback: try muted autoplay. Chromium will refuse unmuted
        // autoplay in some edge cases even inside OBS.
        try {
          el.muted = true
          el.defaultMuted = true
          await el.play()
        } catch (retryError) {
          this.processError(retryError)
        }
      }
    },

    reportStart () {
      reportAdEvent(this.startUrl, 'startUrl')
    },

    reportEnd () {
      reportAdEvent(this.endUrl, 'endUrl')
    },

    emitFinished () {
      // Guard against multiple finished emits (e.g. watchdog + ended firing)
      if (this._finished) return
      this._finished = true
      if (this._watchdog) {
        clearTimeout(this._watchdog)
        this._watchdog = null
      }
      this.$emit('finished', this.$vnode.key)
    },

    processError (e) {
      console.error('finishing because of error', e)
      this.reportFailure('play_rejected')
      this.emitFinished()
    },

    /**
     * Report a launch that ended without the ad being seen.
     *
     * Deliberately before emitFinished(), which tears the component down: the video
     * element's readyState and networkState are the whole diagnosis and are gone
     * afterwards.
     */
    reportFailure (reason) {
      if (this._finished) return

      reportAdFailure(reason, {
        failUrl: this.failUrl,
        taskId: this.taskId,
        contentUrl: this.resolvedContentUrl,
        video: this.$refs.videoElement,
        startedAt: this._mountedAt
      })
    }
  }
}
</script>

<style lang="scss">
.video-box {
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  padding: 12px;

  video {
    width: 100%;
    max-height: 100%;
    padding: 0;
    position: relative;
    z-index: 5;
  }
}

.countdown {
  background-color: rgb(182, 118, 230);
  border-radius: 18px;
  color: #fff;
  font-size: 1em;
  font-weight: 300;
  font-family: monospace;
  height: 32px;
  line-height: 32px;
  padding: 0px 7px;
  position: absolute;
  min-width: 32px;
  right: 10px;
  top: 10px;
  text-align: center;
  z-index: 10;
}

.video-qr {
  position: absolute;
  z-index: 10;

  &.rt {
    top: 30px;
    right: 30px;
  }

  &.rb {
    bottom: 30px;
    right: 30px;
  }

  &.lb {
    bottom: 30px;
    left: 30px;
  }

  &.lt {
    top: 30px;
    left: 30px;
  }
}
</style>